import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListSubheader,
  Grid,
  CircularProgress,
  Chip,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Checkbox,
  Collapse,
  InputBase,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import {
  deleteSavedFilter,
  getSavedFilters,
  saveFilter,
  toggleSavedFilterFavorite,
} from "../utils/savedFilters";
import DashboardSavedFiltersPanel from "./dashboard/DashboardSavedFiltersPanel";
import {
  modalPayloadToFilterState,
  filterStateToModalInitial,
  TAG_NO_TAG,
  normalizeSearchResultsTab,
  SEARCH_RESULTS_TAB_DEFAULT,
  OPEN_STATUS_FILTER_LABELS,
  canEnableAllPeriod,
} from "../utils/searchFilterState";
import AdvancedSearchShortcutHint from "./AdvancedSearchShortcutHint";
import { useUsers } from "../hooks/useUsers";
import { useAgents } from "../hooks/useAgents";
import { useAdvancedSearchFilterOptions } from "../hooks/useAdvancedSearchFilterOptions";
import { FILTER_OPTIONS_STALE_MS } from "../hooks/useCachedFilterOptions";
import { getAgentId, isChatBotUser } from "../utils/assigneeTransferField";

function normalizeAgentIds(ids) {
  return []
    .concat(ids || [])
    .map((x) => String(x).trim())
    .filter(Boolean);
}
/** Compara ids numéricos ou string (ex.: tags) sem forçar Number() em textos. */
function multiSelectIdsEqual(a, b) {
  if (Object.is(a, b)) return true;
  if (a == null || b == null) return false;
  const sa = String(a);
  const sb = String(b);
  if (sa === sb) return true;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
  if (sa.trim() === "" || sb.trim() === "") return false;
  return na === nb;
}

function multiSelectIdKey(v) {
  if (v == null) return "";
  const n = Number(v);
  if (Number.isFinite(n) && String(v).trim() !== "" && String(n) === String(v).trim()) {
    return `n:${n}`;
  }
  return `s:${String(v)}`;
}

function InlineMultiSelect({
  label,
  placeholder,
  options,
  value,
  onChange,
  getOptionLabel = (opt) => (opt?.nome ?? opt?.name ?? String(opt?.id ?? opt ?? "")),
  getOptionValue = (opt) => (opt?.id != null ? Number(opt.id) : opt),
  openKey,
  isOpen,
  onToggleOpen,
  searchPlaceholder = "Filtrar...",
  selectAllLabel = "Selecionar todos",
}) {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const isPrimitive = options.length > 0 && (typeof options[0] === "string" || typeof options[0] === "number");
  const selectedKeys = useMemo(
    () => new Set((value || []).map(multiSelectIdKey)),
    [value]
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((opt) => getOptionLabel(opt).toLowerCase().includes(q));
  }, [options, search, getOptionLabel]);

  const getValueFromOption = useCallback(
    (opt) => (isPrimitive ? opt : getOptionValue(opt)),
    [isPrimitive, getOptionValue]
  );

  const isValueSelected = useCallback(
    (v) => selectedKeys.has(multiSelectIdKey(v)),
    [selectedKeys]
  );

  const filteredValues = useMemo(
    () => filteredOptions.map(getValueFromOption),
    [filteredOptions, getValueFromOption]
  );

  const allFilteredSelected =
    filteredValues.length > 0 && filteredValues.every((v) => isValueSelected(v));
  const someFilteredSelected =
    filteredValues.some((v) => isValueSelected(v)) && !allFilteredSelected;

  const removeValue = useCallback(
    (id) => {
      onChange((value || []).filter((v) => !multiSelectIdsEqual(v, id)));
    },
    [onChange, value]
  );

  const handleSelectAllToggle = (e) => {
    e.stopPropagation();
    if (allFilteredSelected) {
      onChange((value || []).filter((v) => !filteredValues.some((fv) => multiSelectIdsEqual(v, fv))));
      return;
    }
    const toAdd = filteredValues.filter((v) => !isValueSelected(v));
    onChange([...(value || []), ...toAdd]);
  };

  const handleToggle = (opt) => {
    const id = getValueFromOption(opt);
    if (isValueSelected(id)) {
      removeValue(id);
      return;
    }
    onChange([...(value || []), id]);
  };

  const handleChipDelete = (event, id, opt) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (opt != null) {
      handleToggle(opt);
      return;
    }
    removeValue(id);
  };

  const isSelected = (opt) => isValueSelected(getValueFromOption(opt));

  const stickySelectAllBg =
    theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100];

  return (
    <Box>
      <Box
        onClick={() => onToggleOpen(openKey === isOpen ? null : openKey)}
        sx={{
          minHeight: 40,
          px: 1.5,
          py: 0.75,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 0.5,
          bgcolor: "background.paper",
          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, flex: 1, minWidth: 0 }}>
          {value.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {placeholder}
            </Typography>
          )}
          {value.length > 0 &&
            value.map((id) => {
              const opt = isPrimitive
                ? id
                : options.find((o) => multiSelectIdsEqual(getOptionValue(o), id));
              const lbl = isPrimitive
                ? String(id)
                : opt
                  ? getOptionLabel(opt)
                  : String(id);
              return (
                <Chip
                  key={multiSelectIdKey(id)}
                  label={lbl}
                  size="small"
                  variant="outlined"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onDelete={(e) => handleChipDelete(e, id, isPrimitive ? id : opt)}
                />
              );
            })}
        </Box>
        {isOpen === openKey ? (
          <ExpandLessIcon fontSize="small" color="action" />
        ) : (
          <ExpandMoreIcon fontSize="small" color="action" />
        )}
      </Box>
      <Collapse in={isOpen === openKey}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderTop: 0,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            maxHeight: 220,
            overflowY: "auto",
            bgcolor: "background.default",
          }}
        >
          {options.length > 10 && (
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: "divider",
                position: "sticky",
                top: 0,
                zIndex: 2,
                bgcolor: "background.paper",
              }}
            >
              <InputBase
                size="small"
                fullWidth
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                startAdornment={<SearchIcon sx={{ mr: 0.5, color: "action.active" }} fontSize="small" />}
                sx={{ fontSize: "0.875rem" }}
              />
            </Box>
          )}
          {filteredOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
              Nenhuma opção
            </Typography>
          ) : (
            <>
              {options.length > 1 && (
                <ListItemButton
                  dense
                  onClick={handleSelectAllToggle}
                  sx={{
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: stickySelectAllBg,
                    position: "sticky",
                    top: options.length > 10 ? 40 : 0,
                    zIndex: 1,
                    boxShadow: `0 1px 0 ${theme.palette.divider}`,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={allFilteredSelected}
                    indeterminate={someFilteredSelected}
                    sx={{ mr: 1, p: 0.5 }}
                    disableRipple
                  />
                  <ListItemText
                    primary={selectAllLabel}
                    primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                  />
                </ListItemButton>
              )}
              {filteredOptions.map((opt) => {
              const selected = isSelected(opt);
              const labelStr = getOptionLabel(opt);
              const optKey = multiSelectIdKey(getValueFromOption(opt));
              return (
                <ListItemButton
                  key={optKey || labelStr}
                  dense
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(opt);
                  }}
                  selected={selected}
                  sx={{ py: 0.5 }}
                >
                  <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} disableRipple />
                  <ListItemText primary={labelStr} primaryTypographyProps={{ variant: "body2" }} />
                </ListItemButton>
              );
            })}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

const STATUS_OPTIONS = [
  "Pendente",
  "Em Atendimento",
  "Contato Futuro",
  "Concluída",
  "Perdida",
  "Cancelada",
];

function flattenTypesTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => {
    const id = n.id;
    const idPai = n.id_pai != null && n.id_pai !== "" ? Number(n.id_pai) : null;
    const nome = n.nome || String(id);
    return [
      { id: Number(id), id_pai: idPai, nome },
      ...flattenTypesTree(n.children || []),
    ];
  });
}

function normalizeIds(ids) {
  return [].concat(ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n));
}

function getDefaultDateRange() {
  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - 30);

  const format = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return {
    start: format(past),
    end: format(today),
  };
}

const AdvancedSearchModal = ({
  open,
  onClose,
  initialQuery = "",
  initialAdvanced = {},
  onApply,
  applyLabel = "Pesquisar",
  showSavedFiltersManager = true,
  /** "dashboard" oculta keyword, aba de resultados e busca em mensagens. */
  variant = "default",
}) => {
  const theme = useTheme();
  const isDashboardVariant = variant === "dashboard";

  // Mesma fonte do seletor do chat: humanos + chatbots (ura=1) + agentes de IA.
  // Não aplica chat.block_*: esses scopes limitam a lista de conversas, não as opções de filtro.
  // Cache longo: reabrir o modal não deve bater no banco nem mostrar spinner.
  const { data: usersResp, isLoading: loadingUsers } = useUsers(1, 1000, "", "todos", {
    enabled: open,
    order: "nome",
    staleTime: FILTER_OPTIONS_STALE_MS,
  });
  const { data: agentsResp, isLoading: loadingAgents } = useAgents(1, 200, "", {
    enabled: open,
    staleTime: FILTER_OPTIONS_STALE_MS,
  });
  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];

  const {
    typesTree,
    channels,
    clientTags,
    products,
    broadcastCampaigns,
    whatsappCampaigns,
    lostReasons,
    isLoading: loadingMeta,
  } = useAdvancedSearchFilterOptions({
    open,
    includeDashboardExtras: isDashboardVariant,
  });

  const [savedFilters, setSavedFilters] = useState([]);
  const [responsavelAnchor, setResponsavelAnchor] = useState(null);
  const [interacaoAnchor, setInteracaoAnchor] = useState(null);
  const [saveName, setSaveName] = useState("");
  const [saveFavorite, setSaveFavorite] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [keyword, setKeyword] = useState("");
  const [statusFilters, setStatusFilters] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [selectedSubtype1Ids, setSelectedSubtype1Ids] = useState([]);
  const [selectedSubtype2Ids, setSelectedSubtype2Ids] = useState([]);
  const [selectedSubtype3Ids, setSelectedSubtype3Ids] = useState([]);
  const [selectedOriginIds, setSelectedOriginIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [selectedInteractionUserIds, setSelectedInteractionUserIds] = useState([]);
  const [selectedInteractionAgentIds, setSelectedInteractionAgentIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tipoResponsavel, setTipoResponsavel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateField, setDateField] = useState("entrada");
  const [allPeriod, setAllPeriod] = useState(false);
  const [includeMessageText, setIncludeMessageText] = useState(false);
  const [resultsTab, setResultsTab] = useState(SEARCH_RESULTS_TAB_DEFAULT);
  const [openSelect, setOpenSelect] = useState(null);
  const [autoDateRangeApplied, setAutoDateRangeApplied] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [selectedWhatsappCampaignIds, setSelectedWhatsappCampaignIds] = useState([]);
  const [selectedMotiveIds, setSelectedMotiveIds] = useState([]);
  const [selectedPotentialIds, setSelectedPotentialIds] = useState([]);
  const [opsPreset, setOpsPreset] = useState("");
  const [staleDays, setStaleDays] = useState(7);

  const refreshSavedFilters = useCallback(() => {
    if (!showSavedFiltersManager) return;
    setSavedFilters(getSavedFilters());
  }, [showSavedFiltersManager]);

  useEffect(() => {
    if (open) refreshSavedFilters();
  }, [open, refreshSavedFilters]);

  const loadingOptions = loadingMeta || (open && (loadingUsers || loadingAgents));
  // Sincronizar com props apenas quando o dialog ABRE (não a cada re-render do pai).
  // Se rodar sempre que initialAdvanced mudar, a seleção do usuário é sobrescrita e "some".
  const prevOpenRef = React.useRef(false);
  useEffect(() => {
    if (!open) {
      setOpenSelect(null);
      setResponsavelAnchor(null);
      setInteracaoAnchor(null);
      setSaveError("");
      prevOpenRef.current = false;
      return;
    }
    const isOpening = !prevOpenRef.current;
    prevOpenRef.current = true;
    if (isOpening) {
      const modalInit = filterStateToModalInitial({
        ...initialAdvanced,
        filters: initialAdvanced?.filters || initialAdvanced?.statusFilters,
      });
      setKeyword(initialQuery || "");
      setStatusFilters(modalInit.statusFilters || []);
      setSelectedTypeIds(normalizeIds(modalInit.ids_tipos));
      setSelectedSubtype1Ids(normalizeIds(modalInit.ids_subtipo1));
      setSelectedSubtype2Ids(normalizeIds(modalInit.ids_subtipo2));
      setSelectedSubtype3Ids(normalizeIds(modalInit.ids_subtipo3));
      setSelectedOriginIds(normalizeIds(modalInit.ids_origens));
      setSelectedUserIds(normalizeIds(modalInit.ids_usuarios));
      setSelectedAgentIds(normalizeAgentIds(modalInit.ids_agentes));
      setSelectedInteractionUserIds(normalizeIds(modalInit.ids_usuarios_interacao));
      setSelectedInteractionAgentIds(normalizeAgentIds(modalInit.ids_agentes_interacao));
      setSelectedTags(Array.isArray(modalInit.tags) ? [...modalInit.tags] : []);
      setSelectedProductIds(normalizeIds(modalInit.ids_produtos));
      setSelectedCampaignIds(normalizeIds(modalInit.ids_campaigns));
      setSelectedWhatsappCampaignIds(normalizeIds(modalInit.ids_whatsapp_campaigns));
      setSelectedMotiveIds(normalizeIds(modalInit.ids_motivos));
      setSelectedPotentialIds(normalizeIds(modalInit.ids_potenciais));
      setOpsPreset(
        ["overdue", "due_today", "stale"].includes(modalInit.ops_preset) ? modalInit.ops_preset : ""
      );
      setStaleDays(Number(modalInit.stale_days) > 0 ? Number(modalInit.stale_days) : 7);
      const adv = modalInit || {};
      if (Array.isArray(adv.filters) && adv.filters.includes("Fila")) {
        setTipoResponsavel("fila");
      } else if (["humanos", "chatbots", "agentes"].includes(adv.tipo_responsavel)) {
        setTipoResponsavel(adv.tipo_responsavel);
      } else {
        setTipoResponsavel("");
      }
      if (modalInit.all_period) {
        setAllPeriod(true);
        setStartDate("");
        setEndDate("");
      } else if (modalInit.data_inicio || modalInit.data_fim) {
        setAllPeriod(false);
        setStartDate(modalInit.data_inicio || "");
        setEndDate(modalInit.data_fim || "");
      } else {
        setAllPeriod(false);
        const range = getDefaultDateRange();
        setStartDate(range.start);
        setEndDate(range.end);
      }
      setDateField(modalInit.date_field === "entrada" ? "entrada" : "atualizacao");
      setIncludeMessageText(Boolean(modalInit.include_message_text));
      setResultsTab(normalizeSearchResultsTab(modalInit.tab));
      setAutoDateRangeApplied(false);
      setSaveName("");
      setSaveFavorite(false);
    }
  }, [open, initialQuery, initialAdvanced]);

  const humanUsers = useMemo(() => (users || []).filter((u) => !isChatBotUser(u)), [users]);
  const chatbotUsers = useMemo(() => (users || []).filter(isChatBotUser), [users]);
  const agentsList = useMemo(
    () =>
      (agents || [])
        .map((agent) => {
          const id = getAgentId(agent);
          if (id == null || String(id).trim() === "") return null;
          return {
            id: String(id),
            label: agent.name ?? agent.nome ?? `Agente #${id}`,
            raw: agent,
          };
        })
        .filter(Boolean),
    [agents]
  );

  const allHumanUsersSelected = useMemo(
    () => humanUsers.length > 0 && humanUsers.every((u) => selectedUserIds.includes(Number(u.id))),
    [humanUsers, selectedUserIds]
  );
  const someHumanUsersSelected = useMemo(
    () => humanUsers.some((u) => selectedUserIds.includes(Number(u.id))) && !allHumanUsersSelected,
    [humanUsers, selectedUserIds, allHumanUsersSelected]
  );
  const allChatbotUsersSelected = useMemo(
    () => chatbotUsers.length > 0 && chatbotUsers.every((u) => selectedUserIds.includes(Number(u.id))),
    [chatbotUsers, selectedUserIds]
  );
  const someChatbotUsersSelected = useMemo(
    () => chatbotUsers.some((u) => selectedUserIds.includes(Number(u.id))) && !allChatbotUsersSelected,
    [chatbotUsers, selectedUserIds, allChatbotUsersSelected]
  );
  const allAgentsSelected = useMemo(
    () =>
      agentsList.length > 0 &&
      agentsList.every((a) => selectedAgentIds.includes(String(a.id))),
    [agentsList, selectedAgentIds]
  );
  const someAgentsSelected = useMemo(
    () =>
      agentsList.some((a) => selectedAgentIds.includes(String(a.id))) && !allAgentsSelected,
    [agentsList, selectedAgentIds, allAgentsSelected]
  );
  const allInteractionUsersSelected = useMemo(
    () =>
      humanUsers.length > 0 &&
      humanUsers.every((u) => selectedInteractionUserIds.includes(Number(u.id))),
    [humanUsers, selectedInteractionUserIds]
  );
  const someInteractionUsersSelected = useMemo(
    () =>
      humanUsers.some((u) => selectedInteractionUserIds.includes(Number(u.id))) &&
      !allInteractionUsersSelected,
    [humanUsers, selectedInteractionUserIds, allInteractionUsersSelected]
  );
  const allInteractionChatbotsSelected = useMemo(
    () =>
      chatbotUsers.length > 0 &&
      chatbotUsers.every((u) => selectedInteractionUserIds.includes(Number(u.id))),
    [chatbotUsers, selectedInteractionUserIds]
  );
  const someInteractionChatbotsSelected = useMemo(
    () =>
      chatbotUsers.some((u) => selectedInteractionUserIds.includes(Number(u.id))) &&
      !allInteractionChatbotsSelected,
    [chatbotUsers, selectedInteractionUserIds, allInteractionChatbotsSelected]
  );
  const allInteractionAgentsSelected = useMemo(
    () =>
      agentsList.length > 0 &&
      agentsList.every((a) => selectedInteractionAgentIds.includes(String(a.id))),
    [agentsList, selectedInteractionAgentIds]
  );
  const someInteractionAgentsSelected = useMemo(
    () =>
      agentsList.some((a) => selectedInteractionAgentIds.includes(String(a.id))) &&
      !allInteractionAgentsSelected,
    [agentsList, selectedInteractionAgentIds, allInteractionAgentsSelected]
  );

  const toggleAllHumanUsers = () => {
    const humanIds = humanUsers.map((u) => Number(u.id));
    if (allHumanUsersSelected) {
      const humanIdSet = new Set(humanIds);
      setSelectedUserIds((prev) => prev.filter((id) => !humanIdSet.has(Number(id))));
      return;
    }
    setTipoResponsavel("");
    setSelectedUserIds((prev) => [...new Set([...prev, ...humanIds])]);
  };

  const toggleAllChatbotUsers = () => {
    const chatbotIds = chatbotUsers.map((u) => Number(u.id));
    if (allChatbotUsersSelected) {
      const chatbotIdSet = new Set(chatbotIds);
      setSelectedUserIds((prev) => prev.filter((id) => !chatbotIdSet.has(Number(id))));
      return;
    }
    setTipoResponsavel("");
    setSelectedUserIds((prev) => [...new Set([...prev, ...chatbotIds])]);
  };

  const toggleAllAgents = () => {
    const agentIds = agentsList.map((a) => String(a.id));
    if (allAgentsSelected) {
      const agentIdSet = new Set(agentIds);
      setSelectedAgentIds((prev) => prev.filter((id) => !agentIdSet.has(String(id))));
      return;
    }
    setTipoResponsavel("");
    setSelectedAgentIds((prev) => [...new Set([...prev.map(String), ...agentIds])]);
  };

  const toggleAllInteractionUsers = () => {
    const humanIds = humanUsers.map((u) => Number(u.id));
    if (allInteractionUsersSelected) {
      const humanIdSet = new Set(humanIds);
      setSelectedInteractionUserIds((prev) => prev.filter((id) => !humanIdSet.has(Number(id))));
      return;
    }
    setSelectedInteractionUserIds((prev) => [...new Set([...prev, ...humanIds])]);
  };

  const toggleAllInteractionChatbots = () => {
    const chatbotIds = chatbotUsers.map((u) => Number(u.id));
    if (allInteractionChatbotsSelected) {
      const chatbotIdSet = new Set(chatbotIds);
      setSelectedInteractionUserIds((prev) => prev.filter((id) => !chatbotIdSet.has(Number(id))));
      return;
    }
    setSelectedInteractionUserIds((prev) => [...new Set([...prev, ...chatbotIds])]);
  };

  const toggleAllInteractionAgents = () => {
    const agentIds = agentsList.map((a) => String(a.id));
    if (allInteractionAgentsSelected) {
      const agentIdSet = new Set(agentIds);
      setSelectedInteractionAgentIds((prev) => prev.filter((id) => !agentIdSet.has(String(id))));
      return;
    }
    setSelectedInteractionAgentIds((prev) => [...new Set([...prev.map(String), ...agentIds])]);
  };

  const agentColor = theme.palette.secondary.main;
  const chatbotColor = theme.palette.warning?.main || "#ed6c02";

  const responsavelSummary = useMemo(() => {
    if (tipoResponsavel === "fila") return "Fila (sem atribuição)";
    if (tipoResponsavel === "agentes") return "Agentes de IA";
    if (tipoResponsavel === "humanos") return "Humanos";
    if (tipoResponsavel === "chatbots") return "ChatBots";
    const total = selectedUserIds.length + selectedAgentIds.length;
    if (total === 0) return "Todos";
    if (total === 1) {
      if (selectedAgentIds.length === 1) {
        const ag = agentsList.find((a) => String(a.id) === String(selectedAgentIds[0]));
        return ag?.label || `Agente ${selectedAgentIds[0]}`;
      }
      const u = users.find((x) => Number(x.id) === Number(selectedUserIds[0]));
      const name = u?.nome || u?.name || `Usuário ${selectedUserIds[0]}`;
      return isChatBotUser(u) ? `${name} (ChatBot)` : name;
    }
    return `${total} responsáveis`;
  }, [
    tipoResponsavel,
    selectedUserIds,
    selectedAgentIds,
    agentsList,
    users,
  ]);

  const interacaoSummary = useMemo(() => {
    const total = selectedInteractionUserIds.length + selectedInteractionAgentIds.length;
    if (total === 0) return "Qualquer operador";
    if (total === 1) {
      if (selectedInteractionAgentIds.length === 1) {
        const ag = agentsList.find(
          (a) => String(a.id) === String(selectedInteractionAgentIds[0])
        );
        return ag?.label || `Agente ${selectedInteractionAgentIds[0]}`;
      }
      const u = users.find((x) => Number(x.id) === Number(selectedInteractionUserIds[0]));
      const name = u?.nome || u?.name || `Operador ${selectedInteractionUserIds[0]}`;
      return isChatBotUser(u) ? `${name} (ChatBot)` : name;
    }
    return `${total} operadores`;
  }, [
    selectedInteractionUserIds,
    selectedInteractionAgentIds,
    agentsList,
    users,
  ]);

  const typesFlat = useMemo(() => flattenTypesTree(typesTree), [typesTree]);
  const rootTypes = useMemo(
    () => typesFlat.filter((t) => t.id_pai == null || t.id_pai === 0),
    [typesFlat]
  );

  const subtype1Options = useMemo(() => {
    if (!selectedTypeIds.length) return [];
    const idSet = new Set(selectedTypeIds.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedTypeIds]);

  const subtype2Options = useMemo(() => {
    if (!selectedSubtype1Ids.length) return [];
    const idSet = new Set(selectedSubtype1Ids.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedSubtype1Ids]);

  const subtype3Options = useMemo(() => {
    if (!selectedSubtype2Ids.length) return [];
    const idSet = new Set(selectedSubtype2Ids.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedSubtype2Ids]);

  const canUseAllPeriod = useMemo(
    () => canEnableAllPeriod(statusFilters),
    [statusFilters]
  );

  useEffect(() => {
    if (!canUseAllPeriod && allPeriod) {
      setAllPeriod(false);
      if (!startDate && !endDate) {
        const range = getDefaultDateRange();
        setStartDate(range.start);
        setEndDate(range.end);
      }
    }
  }, [canUseAllPeriod, allPeriod, startDate, endDate]);

  const tagOptions = useMemo(() => {
    const fromApi = (clientTags || [])
      .map((t) => String(t?.nome ?? t?.name ?? "").trim())
      .filter(Boolean);
    const unique = [...new Set(fromApi)];
    return [{ id: TAG_NO_TAG, nome: "Sem tag" }, ...unique.map((nome) => ({ id: nome, nome }))];
  }, [clientTags]);

  const buildPayload = () => {
    const filtersOut =
      tipoResponsavel === "fila" ? [...statusFilters, "Fila"] : statusFilters;
    const useAllPeriod = Boolean(isDashboardVariant && allPeriod && canUseAllPeriod);
    let dataInicio = useAllPeriod ? "" : startDate || "";
    let dataFim = useAllPeriod ? "" : endDate || "";
    // Dashboard: não forçar janela de 30 dias — o toolbar já define o período.
    if (!isDashboardVariant && !dataInicio && !dataFim) {
      const range = getDefaultDateRange();
      dataInicio = range.start;
      dataFim = range.end;
    }
    return {
      q: isDashboardVariant ? "" : keyword.trim(),
      filters: filtersOut,
      statusFilters,
      data_inicio: dataInicio || undefined,
      data_fim: dataFim || undefined,
      date_field: dateField,
      all_period: useAllPeriod || undefined,
      ids_tipos: selectedTypeIds.length ? selectedTypeIds : undefined,
      ids_subtipo1: selectedSubtype1Ids.length ? selectedSubtype1Ids : undefined,
      ids_subtipo2: selectedSubtype2Ids.length ? selectedSubtype2Ids : undefined,
      ids_subtipo3: selectedSubtype3Ids.length ? selectedSubtype3Ids : undefined,
      ids_origens: selectedOriginIds.length ? selectedOriginIds : undefined,
      ids_usuarios: selectedUserIds.length ? selectedUserIds : undefined,
      ids_agentes: selectedAgentIds.length ? selectedAgentIds : undefined,
      ids_usuarios_interacao: selectedInteractionUserIds.length
        ? selectedInteractionUserIds
        : undefined,
      ids_agentes_interacao: selectedInteractionAgentIds.length
        ? selectedInteractionAgentIds
        : undefined,
      tags: selectedTags.length ? selectedTags : undefined,
      tipo_responsavel:
        tipoResponsavel && ["fila", "humanos", "chatbots", "agentes"].includes(tipoResponsavel)
          ? tipoResponsavel
          : undefined,
      ids_produtos: isDashboardVariant && selectedProductIds.length ? selectedProductIds : undefined,
      ids_campaigns: (() => {
        if (!isDashboardVariant) return undefined;
        const fromWa = [];
        for (const id of selectedWhatsappCampaignIds || []) {
          const opt = whatsappCampaigns.find((c) => Number(c?.id) === Number(id));
          if (opt?.filterTarget === "campaign") fromWa.push(Number(id));
        }
        const merged = [
          ...new Set([...(selectedCampaignIds || []).map(Number), ...fromWa].filter((n) => n > 0)),
        ];
        return merged.length ? merged : undefined;
      })(),
      ids_whatsapp_campaigns: (() => {
        if (!isDashboardVariant) return undefined;
        const legacy = [];
        for (const id of selectedWhatsappCampaignIds || []) {
          const opt = whatsappCampaigns.find((c) => Number(c?.id) === Number(id));
          if (!opt || opt.filterTarget === "whatsapp_campaign") legacy.push(Number(id));
        }
        return legacy.length ? legacy : undefined;
      })(),
      ids_motivos: isDashboardVariant && selectedMotiveIds.length ? selectedMotiveIds : undefined,
      ids_potenciais:
        isDashboardVariant && selectedPotentialIds.length ? selectedPotentialIds : undefined,
      ops_preset: isDashboardVariant && opsPreset ? opsPreset : undefined,
      stale_days: isDashboardVariant && opsPreset === "stale" ? staleDays || 7 : undefined,
      include_message_text: isDashboardVariant ? undefined : includeMessageText || undefined,
      tab: isDashboardVariant ? undefined : resultsTab,
    };
  };

  const handleApply = () => {
    const payload = buildPayload();
    if (typeof onApply === "function") onApply(payload);
    onClose();
  };

  const handleSaveCurrentFilter = () => {
    setSaveError("");
    try {
      const state = modalPayloadToFilterState(buildPayload());
      saveFilter({ nome: saveName, state, favorito: saveFavorite });
      setSaveName("");
      setSaveFavorite(false);
      refreshSavedFilters();
    } catch (err) {
      setSaveError(err?.message || "Não foi possível salvar o filtro");
    }
  };

  const handleLoadSavedFilter = (entry) => {
    if (!entry?.state) return;
    const modalInit = filterStateToModalInitial(entry.state);
    setKeyword(entry.state.q || "");
    setStatusFilters(modalInit.statusFilters || []);
    setSelectedTypeIds(normalizeIds(modalInit.ids_tipos));
    setSelectedSubtype1Ids(normalizeIds(modalInit.ids_subtipo1));
    setSelectedSubtype2Ids(normalizeIds(modalInit.ids_subtipo2));
    setSelectedSubtype3Ids(normalizeIds(modalInit.ids_subtipo3));
    setSelectedOriginIds(normalizeIds(modalInit.ids_origens));
    setSelectedUserIds(normalizeIds(modalInit.ids_usuarios));
    setSelectedAgentIds(normalizeAgentIds(modalInit.ids_agentes));
    setSelectedInteractionUserIds(normalizeIds(modalInit.ids_usuarios_interacao));
    setSelectedInteractionAgentIds(normalizeAgentIds(modalInit.ids_agentes_interacao));
    setSelectedTags(Array.isArray(modalInit.tags) ? [...modalInit.tags] : []);
    setTipoResponsavel(modalInit.tipo_responsavel || "");
    setStartDate(modalInit.data_inicio || "");
    setEndDate(modalInit.data_fim || "");
    setDateField(modalInit.date_field === "entrada" ? "entrada" : "atualizacao");
    setAllPeriod(Boolean(modalInit.all_period));
    setIncludeMessageText(Boolean(modalInit.include_message_text));
    setResultsTab(normalizeSearchResultsTab(modalInit.tab));
    if (isDashboardVariant) {
      setSelectedProductIds(normalizeIds(modalInit.ids_produtos));
      setSelectedCampaignIds(normalizeIds(modalInit.ids_campaigns));
      setSelectedWhatsappCampaignIds(normalizeIds(modalInit.ids_whatsapp_campaigns));
      setSelectedMotiveIds(normalizeIds(modalInit.ids_motivos));
      setSelectedPotentialIds(normalizeIds(modalInit.ids_potenciais));
      setOpsPreset(String(modalInit.ops_preset || "").trim());
      setStaleDays(Number(modalInit.stale_days) > 0 ? Number(modalInit.stale_days) : 7);
    }
  };

  const handleClearFilters = () => {
    setKeyword("");
    setStatusFilters([]);
    setSelectedTypeIds([]);
    setSelectedSubtype1Ids([]);
    setSelectedSubtype2Ids([]);
    setSelectedSubtype3Ids([]);
    setSelectedOriginIds([]);
    setSelectedUserIds([]);
    setSelectedAgentIds([]);
    setSelectedInteractionUserIds([]);
    setSelectedInteractionAgentIds([]);
    setSelectedTags([]);
    setSelectedProductIds([]);
    setSelectedCampaignIds([]);
    setSelectedWhatsappCampaignIds([]);
    setSelectedMotiveIds([]);
    setSelectedPotentialIds([]);
    setOpsPreset("");
    setStaleDays(7);
    setTipoResponsavel("");
    setAllPeriod(false);
    const range = getDefaultDateRange();
    setStartDate(range.start);
    setEndDate(range.end);
    setDateField("entrada");
    setIncludeMessageText(false);
    setResultsTab(SEARCH_RESULTS_TAB_DEFAULT);
    setAutoDateRangeApplied(false);
  };

  const hasActiveFilters =
    keyword.trim() ||
    statusFilters.length > 0 ||
    selectedTypeIds.length > 0 ||
    selectedSubtype1Ids.length > 0 ||
    selectedSubtype2Ids.length > 0 ||
    selectedSubtype3Ids.length > 0 ||
    selectedOriginIds.length > 0 ||
    selectedUserIds.length > 0 ||
    selectedAgentIds.length > 0 ||
    selectedInteractionUserIds.length > 0 ||
    selectedInteractionAgentIds.length > 0 ||
    selectedTags.length > 0 ||
    selectedProductIds.length > 0 ||
    selectedCampaignIds.length > 0 ||
    selectedWhatsappCampaignIds.length > 0 ||
    selectedMotiveIds.length > 0 ||
    selectedPotentialIds.length > 0 ||
    Boolean(opsPreset) ||
    tipoResponsavel !== "" ||
    allPeriod ||
    startDate ||
    endDate ||
    includeMessageText;

  const sectionSx = {
    p: 2,
    borderRadius: 2,
    bgcolor: "action.hover",
    "&:empty": { display: "none" },
  };
  const fieldLabelSx = { mb: 0.75, fontWeight: 500, color: "text.secondary" };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: 24,
        },
      }}
      disableScrollLock
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
          px: 2.5,
          pr: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          <TuneRoundedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              display: "block",
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
            }}
          >
            {isDashboardVariant ? "Filtrar solicitações" : "Busca avançada"}
          </Typography>
          {isDashboardVariant ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "block", fontWeight: 400, mt: 0.4, fontSize: "0.8125rem", lineHeight: 1.35 }}
            >
              Os indicadores desta aba passam a refletir só o que você marcar
            </Typography>
          ) : null}
        </Box>
      </DialogTitle>

      {!isDashboardVariant ? <AdvancedSearchShortcutHint open={open} /> : null}

      <DialogContent
        sx={{
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          px: 0,
          pt: 0,
          pb: 0,
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        {loadingOptions ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box
            sx={{
              overflowY: "auto",
              overflowX: "hidden",
              px: 3,
              pt: 3,
              pb: 2,
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Seção: Busca por texto — oculto no variant dashboard */}
            {!isDashboardVariant ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ ...fieldLabelSx, display: "block" }}>
                Palavra-chave
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Digite termos para buscar..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Box sx={{ mt: 1.25 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5, lineHeight: 1.3 }}
                >
                  Abrir resultados em
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={resultsTab}
                  onChange={(_, value) => {
                    if (value) setResultsTab(value);
                  }}
                  aria-label="Aba de resultados da busca"
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    "& .MuiToggleButtonGroup-grouped": {
                      border: 0,
                      borderRadius: "999px !important",
                      mx: 0,
                    },
                    "& .MuiToggleButton-root": {
                      px: 1.25,
                      py: 0.35,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      lineHeight: 1.4,
                      textTransform: "none",
                      color: "text.secondary",
                      bgcolor: "action.hover",
                      "&.Mui-selected": {
                        color: "primary.main",

                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(25, 118, 210, 0.22)"
                            : "rgba(25, 118, 210, 0.12)",
                        "&:hover": {
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(25, 118, 210, 0.28)"
                              : "rgba(25, 118, 210, 0.16)",
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="atendimento">Atendimento</ToggleButton>
                  <ToggleButton value="compras">Compras</ToggleButton>
                  <ToggleButton value="clientes">Clientes</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            ) : null}

            <Grid container spacing={3}>
              {/* Coluna esquerda: Status e Tipo */}
              <Grid item xs={12} md={6}>
                <Box sx={sectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "text.primary" }}>
                    Status e tipo
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Status</Typography>
                      <InlineMultiSelect
                        placeholder="Selecione os status..."
                        options={STATUS_OPTIONS}
                        value={statusFilters}
                        onChange={setStatusFilters}
                        getOptionLabel={(opt) => String(opt)}
                        openKey="status"
                        isOpen={openSelect}
                        onToggleOpen={setOpenSelect}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Responsável</Typography>
                      <Box
                        onClick={(e) => setResponsavelAnchor(e.currentTarget)}
                        sx={{
                          minHeight: 40,
                          px: 1.5,
                          py: 0.75,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 0.5,
                          bgcolor: "background.paper",
                          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          color={
                            tipoResponsavel || selectedUserIds.length || selectedAgentIds.length
                              ? "text.primary"
                              : "text.secondary"
                          }
                        >
                          {responsavelSummary}
                        </Typography>
                        <ExpandMoreIcon fontSize="small" color="action" />
                      </Box>
                      <Menu
                        anchorEl={responsavelAnchor}
                        open={Boolean(responsavelAnchor)}
                        onClose={() => setResponsavelAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        PaperProps={{ sx: { maxHeight: 400, minWidth: 280 } }}
                      >
                        <MenuItem
                          onClick={() => {
                            setTipoResponsavel("");
                            setSelectedUserIds([]);
                            setSelectedAgentIds([]);
                            setResponsavelAnchor(null);
                          }}
                        >
                          Todos
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setTipoResponsavel("fila");
                            setSelectedUserIds([]);
                            setSelectedAgentIds([]);
                            setResponsavelAnchor(null);
                          }}
                        >
                          Fila (sem atribuição)
                        </MenuItem>
                        {agentsList.length > 0 && [
                          <ListSubheader key="sh-agent" disableSticky sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            Agentes de IA
                          </ListSubheader>,
                          <MenuItem
                            key="opt-agentes"
                            onClick={() => {
                              setTipoResponsavel("agentes");
                              setSelectedUserIds([]);
                              setSelectedAgentIds([]);
                              setResponsavelAnchor(null);
                            }}
                          >
                            Agentes de IA (qualquer)
                          </MenuItem>,
                          agentsList.length > 1 && (
                            <MenuItem
                              key="opt-agentes-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAllAgents();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={allAgentsSelected}
                                indeterminate={someAgentsSelected}
                                sx={{ mr: 1, p: 0 }}
                              />
                              <ListItemText
                                primary="Selecionar todos"
                                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                              />
                            </MenuItem>
                          ),
                          ...agentsList.map((a) => {
                            const checked = selectedAgentIds.includes(String(a.id));
                            return (
                              <MenuItem
                                key={`ag-${a.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTipoResponsavel("");
                                  setSelectedAgentIds((prev) =>
                                    checked
                                      ? prev.filter((x) => String(x) !== String(a.id))
                                      : [...prev, String(a.id)]
                                  );
                                }}
                                sx={{ color: agentColor, fontWeight: "bold" }}
                              >
                                <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0 }} />
                                <ListItemText
                                  primary={a.label}
                                  primaryTypographyProps={{ color: "inherit", fontWeight: "inherit" }}
                                />
                              </MenuItem>
                            );
                          }),
                        ]}
                        {humanUsers.length > 0 && [
                          <ListSubheader key="sh-humanos" disableSticky sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            Usuários
                          </ListSubheader>,
                          <MenuItem
                            key="opt-humanos"
                            onClick={() => {
                              setTipoResponsavel("humanos");
                              setSelectedUserIds([]);
                              setSelectedAgentIds([]);
                              setResponsavelAnchor(null);
                            }}
                          >
                            Humanos (qualquer)
                          </MenuItem>,
                          humanUsers.length > 1 && (
                            <MenuItem
                              key="opt-humanos-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAllHumanUsers();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={allHumanUsersSelected}
                                indeterminate={someHumanUsersSelected}
                                sx={{ mr: 1, p: 0 }}
                              />
                              <ListItemText
                                primary="Selecionar todos"
                                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                              />
                            </MenuItem>
                          ),
                          ...humanUsers.map((u) => {
                            const id = Number(u.id);
                            const checked = selectedUserIds.includes(id);
                            return (
                              <MenuItem
                                key={`u-${id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTipoResponsavel("");
                                  setSelectedUserIds((prev) =>
                                    checked ? prev.filter((x) => Number(x) !== id) : [...prev, id]
                                  );
                                }}
                              >
                                <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0 }} />
                                <ListItemText primary={u.nome ?? u.name ?? `Usuário ${id}`} />
                              </MenuItem>
                            );
                          }),
                        ]}
                        {chatbotUsers.length > 0 && [
                          <ListSubheader key="sh-chatbots" disableSticky sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            ChatBots
                          </ListSubheader>,
                          <MenuItem
                            key="opt-chatbots"
                            onClick={() => {
                              setTipoResponsavel("chatbots");
                              setSelectedUserIds([]);
                              setSelectedAgentIds([]);
                              setResponsavelAnchor(null);
                            }}
                          >
                            ChatBots (qualquer)
                          </MenuItem>,
                          chatbotUsers.length > 1 && (
                            <MenuItem
                              key="opt-chatbots-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAllChatbotUsers();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={allChatbotUsersSelected}
                                indeterminate={someChatbotUsersSelected}
                                sx={{ mr: 1, p: 0 }}
                              />
                              <ListItemText
                                primary="Selecionar todos"
                                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                              />
                            </MenuItem>
                          ),
                          ...chatbotUsers.map((u) => {
                            const id = Number(u.id);
                            const checked = selectedUserIds.includes(id);
                            return (
                              <MenuItem
                                key={`cb-${id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTipoResponsavel("");
                                  setSelectedUserIds((prev) =>
                                    checked ? prev.filter((x) => Number(x) !== id) : [...prev, id]
                                  );
                                }}
                                sx={{ color: chatbotColor, fontWeight: 500 }}
                              >
                                <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0 }} />
                                <ListItemText
                                  primary={`${u.nome ?? u.name ?? `ChatBot ${id}`} (ChatBot)`}
                                  primaryTypographyProps={{ color: "inherit", fontWeight: "inherit" }}
                                />
                              </MenuItem>
                            );
                          }),
                        ]}
                      </Menu>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>
                        Operadores que interagiram
                      </Typography>
                      <Box
                        onClick={(e) => setInteracaoAnchor(e.currentTarget)}
                        sx={{
                          minHeight: 40,
                          px: 1.5,
                          py: 0.75,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 0.5,
                          bgcolor: "background.paper",
                          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          color={
                            selectedInteractionUserIds.length || selectedInteractionAgentIds.length
                              ? "text.primary"
                              : "text.secondary"
                          }
                        >
                          {interacaoSummary}
                        </Typography>
                        <ExpandMoreIcon fontSize="small" color="action" />
                      </Box>
                      <Menu
                        anchorEl={interacaoAnchor}
                        open={Boolean(interacaoAnchor)}
                        onClose={() => setInteracaoAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        PaperProps={{ sx: { maxHeight: 360, minWidth: 280 } }}
                      >
                        <MenuItem
                          onClick={() => {
                            setSelectedInteractionUserIds([]);
                            setSelectedInteractionAgentIds([]);
                            setInteracaoAnchor(null);
                          }}
                        >
                          Qualquer operador
                        </MenuItem>
                        {agentsList.length > 0 && [
                          <ListSubheader key="int-sh-agent" disableSticky sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            Agentes de IA
                          </ListSubheader>,
                          agentsList.length > 1 && (
                            <MenuItem
                              key="int-agentes-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAllInteractionAgents();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={allInteractionAgentsSelected}
                                indeterminate={someInteractionAgentsSelected}
                                sx={{ mr: 1, p: 0 }}
                              />
                              <ListItemText
                                primary="Selecionar todos"
                                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                              />
                            </MenuItem>
                          ),
                          ...agentsList.map((a) => {
                            const checked = selectedInteractionAgentIds.includes(String(a.id));
                            return (
                              <MenuItem
                                key={`int-ag-${a.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInteractionAgentIds((prev) =>
                                    checked
                                      ? prev.filter((x) => String(x) !== String(a.id))
                                      : [...prev, String(a.id)]
                                  );
                                }}
                                sx={{ color: agentColor, fontWeight: "bold" }}
                              >
                                <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0 }} />
                                <ListItemText
                                  primary={a.label}
                                  primaryTypographyProps={{ color: "inherit", fontWeight: "inherit" }}
                                />
                              </MenuItem>
                            );
                          }),
                        ]}
                        {humanUsers.length > 0 && [
                          <ListSubheader key="int-sh-humanos" disableSticky sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            Usuários
                          </ListSubheader>,
                          humanUsers.length > 1 && (
                            <MenuItem
                              key="int-humanos-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAllInteractionUsers();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={allInteractionUsersSelected}
                                indeterminate={someInteractionUsersSelected}
                                sx={{ mr: 1, p: 0 }}
                              />
                              <ListItemText
                                primary="Selecionar todos"
                                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                              />
                            </MenuItem>
                          ),
                          ...humanUsers.map((u) => {
                            const id = Number(u.id);
                            const checked = selectedInteractionUserIds.includes(id);
                            return (
                              <MenuItem
                                key={`int-${id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInteractionUserIds((prev) =>
                                    checked ? prev.filter((x) => Number(x) !== id) : [...prev, id]
                                  );
                                }}
                              >
                                <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0 }} />
                                <ListItemText primary={u.nome ?? u.name ?? `Operador ${id}`} />
                              </MenuItem>
                            );
                          }),
                        ]}
                        {chatbotUsers.length > 0 && [
                          <ListSubheader key="int-sh-chatbots" disableSticky sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            ChatBots
                          </ListSubheader>,
                          chatbotUsers.length > 1 && (
                            <MenuItem
                              key="int-chatbots-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAllInteractionChatbots();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={allInteractionChatbotsSelected}
                                indeterminate={someInteractionChatbotsSelected}
                                sx={{ mr: 1, p: 0 }}
                              />
                              <ListItemText
                                primary="Selecionar todos"
                                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                              />
                            </MenuItem>
                          ),
                          ...chatbotUsers.map((u) => {
                            const id = Number(u.id);
                            const checked = selectedInteractionUserIds.includes(id);
                            return (
                              <MenuItem
                                key={`int-cb-${id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInteractionUserIds((prev) =>
                                    checked ? prev.filter((x) => Number(x) !== id) : [...prev, id]
                                  );
                                }}
                                sx={{ color: chatbotColor, fontWeight: 500 }}
                              >
                                <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0 }} />
                                <ListItemText
                                  primary={`${u.nome ?? u.name ?? `ChatBot ${id}`} (ChatBot)`}
                                  primaryTypographyProps={{ color: "inherit", fontWeight: "inherit" }}
                                />
                              </MenuItem>
                            );
                          }),
                        ]}
                      </Menu>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Tipo</Typography>
                      <InlineMultiSelect
                        placeholder="Selecione os tipos..."
                        options={rootTypes}
                        value={selectedTypeIds}
                        onChange={(ids) => {
                          setSelectedTypeIds(ids);
                          if (!ids.length) {
                            setSelectedSubtype1Ids([]);
                            setSelectedSubtype2Ids([]);
                            setSelectedSubtype3Ids([]);
                          }
                        }}
                        getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                        getOptionValue={(o) => Number(o?.id)}
                        openKey="type"
                        isOpen={openSelect}
                        onToggleOpen={setOpenSelect}
                        searchPlaceholder="Filtrar tipos..."
                      />
                    </Box>
                    {subtype1Options.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={fieldLabelSx}>Subtipo 1</Typography>
                        <InlineMultiSelect
                          placeholder="Selecione..."
                          options={subtype1Options}
                          value={selectedSubtype1Ids}
                          onChange={(ids) => {
                            setSelectedSubtype1Ids(ids);
                            if (!ids.length) {
                              setSelectedSubtype2Ids([]);
                              setSelectedSubtype3Ids([]);
                            }
                          }}
                          getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                          getOptionValue={(o) => Number(o?.id)}
                          openKey="subtype1"
                          isOpen={openSelect}
                          onToggleOpen={setOpenSelect}
                          searchPlaceholder="Filtrar..."
                        />
                      </Box>
                    )}
                    {subtype2Options.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={fieldLabelSx}>Subtipo 2</Typography>
                        <InlineMultiSelect
                          placeholder="Selecione..."
                          options={subtype2Options}
                          value={selectedSubtype2Ids}
                          onChange={(ids) => {
                            setSelectedSubtype2Ids(ids);
                            if (!ids.length) setSelectedSubtype3Ids([]);
                          }}
                          getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                          getOptionValue={(o) => Number(o?.id)}
                          openKey="subtype2"
                          isOpen={openSelect}
                          onToggleOpen={setOpenSelect}
                          searchPlaceholder="Filtrar..."
                        />
                      </Box>
                    )}
                    {subtype3Options.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={fieldLabelSx}>Subtipo 3</Typography>
                        <InlineMultiSelect
                          placeholder="Selecione..."
                          options={subtype3Options}
                          value={selectedSubtype3Ids}
                          onChange={setSelectedSubtype3Ids}
                          getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                          getOptionValue={(o) => Number(o?.id)}
                          openKey="subtype3"
                          isOpen={openSelect}
                          onToggleOpen={setOpenSelect}
                          searchPlaceholder="Filtrar..."
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Coluna direita: Canal, Período */}
              <Grid item xs={12} md={6}>
                <Box sx={sectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "text.primary" }}>
                    Origem e período
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Canal / Origem</Typography>
                      <InlineMultiSelect
                        placeholder="Selecione os canais..."
                        options={channels}
                        value={selectedOriginIds}
                        onChange={setSelectedOriginIds}
                        getOptionLabel={(o) => o?.nome ?? o?.name ?? String(o?.id ?? "")}
                        getOptionValue={(o) => Number(o?.id)}
                        openKey="channel"
                        isOpen={openSelect}
                        onToggleOpen={setOpenSelect}
                        searchPlaceholder="Filtrar canais..."
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Tags da solicitação</Typography>
                      <InlineMultiSelect
                        placeholder="Selecione as tags..."
                        options={tagOptions}
                        value={selectedTags}
                        onChange={setSelectedTags}
                        getOptionLabel={(o) => o?.nome ?? String(o?.id ?? o ?? "")}
                        getOptionValue={(o) => (o?.id != null ? String(o.id) : String(o))}
                        openKey="tags"
                        isOpen={openSelect}
                        onToggleOpen={setOpenSelect}
                        searchPlaceholder="Filtrar tags..."
                      />
                    </Box>
                    {isDashboardVariant ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
                          Operação e comercial
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                          <InputLabel>Atalho operacional</InputLabel>
                          <Select
                            value={opsPreset}
                            label="Atalho operacional"
                            onChange={(e) => setOpsPreset(e.target.value)}
                          >
                            <MenuItem value="">Nenhum</MenuItem>
                            <MenuItem value="overdue">Atrasados</MenuItem>
                            <MenuItem value="due_today">Para hoje</MenuItem>
                            <MenuItem value="stale">Sem movimentação</MenuItem>
                          </Select>
                        </FormControl>
                        {opsPreset === "stale" ? (
                          <TextField
                            label="Dias sem movimentação"
                            type="number"
                            size="small"
                            value={staleDays}
                            onChange={(e) => setStaleDays(Math.max(1, Number(e.target.value) || 7))}
                            inputProps={{ min: 1, max: 365 }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                          />
                        ) : null}
                        <Box>
                          <Typography variant="subtitle2" sx={fieldLabelSx}>Potencial</Typography>
                          <InlineMultiSelect
                            placeholder="Selecione..."
                            options={[
                              { id: 1, nome: "Baixo" },
                              { id: 2, nome: "Médio" },
                              { id: 3, nome: "Alto" },
                            ]}
                            value={selectedPotentialIds}
                            onChange={setSelectedPotentialIds}
                            getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                            getOptionValue={(o) => Number(o?.id)}
                            openKey="potencial"
                            isOpen={openSelect}
                            onToggleOpen={setOpenSelect}
                            searchPlaceholder="Filtrar..."
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={fieldLabelSx}>Produto</Typography>
                          <InlineMultiSelect
                            placeholder="Selecione produtos..."
                            options={products}
                            value={selectedProductIds}
                            onChange={setSelectedProductIds}
                            getOptionLabel={(o) => o?.nome ?? o?.name ?? String(o?.id ?? "")}
                            getOptionValue={(o) => Number(o?.id)}
                            openKey="products"
                            isOpen={openSelect}
                            onToggleOpen={setOpenSelect}
                            searchPlaceholder="Filtrar produtos..."
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={fieldLabelSx}>Campanha (broadcast)</Typography>
                          <InlineMultiSelect
                            placeholder="Selecione campanhas..."
                            options={broadcastCampaigns}
                            value={selectedCampaignIds}
                            onChange={setSelectedCampaignIds}
                            getOptionLabel={(o) => o?.name ?? o?.nome ?? String(o?.id ?? "")}
                            getOptionValue={(o) => Number(o?.id)}
                            openKey="campaigns"
                            isOpen={openSelect}
                            onToggleOpen={setOpenSelect}
                            searchPlaceholder="Filtrar campanhas..."
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={fieldLabelSx}>Campanha WhatsApp</Typography>
                          <InlineMultiSelect
                            placeholder="Selecione campanhas..."
                            options={whatsappCampaigns}
                            value={selectedWhatsappCampaignIds}
                            onChange={setSelectedWhatsappCampaignIds}
                            getOptionLabel={(o) => o?.name ?? o?.nome ?? String(o?.id ?? "")}
                            getOptionValue={(o) => Number(o?.id)}
                            openKey="waCampaigns"
                            isOpen={openSelect}
                            onToggleOpen={setOpenSelect}
                            searchPlaceholder="Filtrar campanhas..."
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={fieldLabelSx}>Motivo de perda</Typography>
                          <InlineMultiSelect
                            placeholder="Selecione motivos..."
                            options={lostReasons}
                            value={selectedMotiveIds}
                            onChange={setSelectedMotiveIds}
                            getOptionLabel={(o) => o?.nome ?? o?.name ?? String(o?.id ?? "")}
                            getOptionValue={(o) => Number(o?.id)}
                            openKey="motives"
                            isOpen={openSelect}
                            onToggleOpen={setOpenSelect}
                            searchPlaceholder="Filtrar motivos..."
                          />
                        </Box>
                      </Box>
                    ) : null}
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Data de referência</Typography>
                      <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                        <InputLabel>Referência</InputLabel>
                        <Select
                          value={dateField}
                          onChange={(e) => setDateField(e.target.value)}
                          label="Referência"
                          disabled={isDashboardVariant && allPeriod}
                        >
                          <MenuItem value="atualizacao">Última atualização</MenuItem>
                          <MenuItem value="entrada">Data de entrada</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {isDashboardVariant && canUseAllPeriod ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: allPeriod ? "action.selected" : "action.hover",
                          border: 1,
                          borderColor: allPeriod ? "primary.main" : "transparent",
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={allPeriod}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAllPeriod(checked);
                            setAutoDateRangeApplied(false);
                            if (checked) {
                              setStartDate("");
                              setEndDate("");
                            } else if (!startDate && !endDate) {
                              const range = getDefaultDateRange();
                              setStartDate(range.start);
                              setEndDate(range.end);
                            }
                          }}
                          sx={{ mt: -0.25 }}
                        />
                        <Box>
                          <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 0.25 }}>
                            Todo o histórico em aberto
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                            Ignora as datas e inclui todas as solicitações em aberto
                            {statusFilters.filter((s) => OPEN_STATUS_FILTER_LABELS.includes(s)).length
                              ? ` (${statusFilters
                                  .filter((s) => OPEN_STATUS_FILTER_LABELS.includes(s))
                                  .join(", ")})`
                              : ""}
                            . Ideal para acompanhar a fila completa.
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          label="De"
                          type="date"
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={startDate}
                          onChange={(e) => {
                            setAllPeriod(false);
                            setStartDate(e.target.value);
                          }}
                          disabled={isDashboardVariant && allPeriod}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Até"
                          type="date"
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={endDate}
                          onChange={(e) => {
                            setAllPeriod(false);
                            setEndDate(e.target.value);
                          }}
                          disabled={isDashboardVariant && allPeriod}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                    </Grid>
                    {isDashboardVariant && allPeriod ? (
                      <Typography variant="caption" color="text.secondary">
                        Enquanto isso estiver ativo, as datas da barra superior ficam pausadas.
                      </Typography>
                    ) : null}
                    {autoDateRangeApplied && (
                      <Typography
                        variant="caption"
                        sx={{ mt: 0.5 }}
                        color="error"
                      >
                        Aplicamos automaticamente um filtro de data nos últimos 30 dias para
                        deixar esta busca mais leve. Ajuste o período se quiser ampliar ou
                        reduzir o alcance.
                      </Typography>
                    )}
                    <Box>
                      {isDashboardVariant ? null : (
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Checkbox
                          size="small"
                          checked={includeMessageText}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setIncludeMessageText(checked);
                            if (checked && !startDate && !endDate) {
                              const range = getDefaultDateRange();
                              setStartDate(range.start);
                              setEndDate(range.end);
                              setAutoDateRangeApplied(true);
                            }
                          }}
                        />
                        <Box>
                          <Typography variant="subtitle2" sx={fieldLabelSx}>
                            Incluir texto das mensagens
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ao ativar esta opção, a busca passa a analisar também o conteúdo completo
                            das mensagens da conversa. Em históricos muitos grandes isso torna a pesquisa
                            mais pesada e pode reduzir a velocidade de resposta da tela.
                            Para manter um bom desempenho, sugerimos um período recente por padrão, mas
                            você pode ajustar as datas acima conforme a sua necessidade.
                          </Typography>
                        </Box>
                      </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {isDashboardVariant ? (
              <DashboardSavedFiltersPanel
                section="atendimento"
                getSnapshot={() => modalPayloadToFilterState(buildPayload())}
                onApply={(state) => {
                  if (typeof onApply === "function") {
                    onApply(filterStateToModalInitial(state));
                  }
                  onClose();
                }}
              />
            ) : null}

            {showSavedFiltersManager && !isDashboardVariant && (
              <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <BookmarkBorderIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Filtros salvos
                  </Typography>
                </Box>
                {savedFilters.length > 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                    {savedFilters.map((entry) => (
                      <Box
                        key={entry.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          "&:hover": { bgcolor: "background.paper" },
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label={entry.favorito ? "Remover favorito" : "Marcar favorito"}
                          onClick={() => {
                            toggleSavedFilterFavorite(entry.id);
                            refreshSavedFilters();
                          }}
                        >
                          {entry.favorito ? (
                            <StarIcon fontSize="small" color="warning" />
                          ) : (
                            <StarBorderIcon fontSize="small" />
                          )}
                        </IconButton>
                        <ListItemButton
                          dense
                          onClick={() => handleLoadSavedFilter(entry)}
                          sx={{ flex: 1, borderRadius: 1, py: 0.25 }}
                        >
                          <ListItemText
                            primary={entry.nome}
                            secondary="Clique para carregar esta combinação"
                            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItemButton>
                        <IconButton
                          size="small"
                          aria-label="Excluir filtro salvo"
                          onClick={() => {
                            deleteSavedFilter(entry.id);
                            refreshSavedFilters();
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Salve combinações usadas com frequência para aplicá-las com um clique.
                  </Typography>
                )}
                <Typography variant="subtitle2" sx={{ ...fieldLabelSx, display: "block" }}>
                  Salvar combinação atual
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <TextField
                    size="small"
                    placeholder="Nome do filtro (ex.: Pendentes WhatsApp)"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    sx={{ flex: "1 1 220px", minWidth: 180 }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pt: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={saveFavorite}
                      onChange={(e) => setSaveFavorite(e.target.checked)}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Favorito
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSaveCurrentFilter}
                    disabled={!saveName.trim()}
                    sx={{ alignSelf: "center" }}
                  >
                    Salvar
                  </Button>
                </Box>
                {saveError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    {saveError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: "divider",
          px: 3,
          py: 2,
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleClearFilters}
          color="inherit"
          size="medium"
          disabled={!hasActiveFilters}
          sx={{ textTransform: "none" }}
        >
          Limpar todos os filtros
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} size="medium" sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          size="medium"
          startIcon={isDashboardVariant ? <TuneRoundedIcon /> : <SearchRoundedIcon />}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {applyLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedSearchModal;
